const isDevBypass = () =>
  process.env.NODE_ENV === 'development' &&
  process.env.NEXT_PUBLIC_DEV_BYPASS_AUTH === 'true';

// Fast cookie-presence check for SSR auth gating.
//
// Why not validate the JWT signature here? `runWithAmplifyServerContext +
// fetchAuthSession` was taking ~3.7s on every navigation because each call
// runs in its own Vercel serverless function (cold-start + adapter init +
// Cognito refresh). Next.js holds the URL change until getServerSideProps
// resolves, so back-button navigation showed a 4.6s blank-page lag — the
// new design's `cache-and-network` perf fix never got to render cached
// data because the page hadn't even mounted yet.
//
// The SSR gate doesn't need cryptographic validation. Its job is to redirect
// missing-cookie users to /. Real validation happens at API call time:
//   - /api/graphql attaches the JWT and the backend validates the signature
//   - UserInfoContext bounces 401s back to /
//   - api/session-info/* re-validate via runWithAmplifyServerContext
//
// A forged cookie that passes presence-only check just gets bounced at the
// first GraphQL fetch — no security regression, just a brief UI flicker
// before redirect.
export const getServerSideAuth = function (context) {
  if (isDevBypass()) {
    const url = context?.req?.url || '';
    const isHomePage = url === '/' || url.includes('/index.json');
    return !isHomePage;
  }

  const cookieHeader = context?.req?.headers?.cookie;
  if (!cookieHeader) return false;

  // Amplify v6 with ssr:true writes cookies as
  // `CognitoIdentityServiceProvider.{clientId}.{username}.{tokenType}`.
  const clientId = process.env.USER_POOL_CLIENT_ID;
  if (!clientId) return false;
  const prefix = `CognitoIdentityServiceProvider.${clientId}.`;

  return cookieHeader.split(';').some((c) => {
    const trimmed = c.trim();
    return (
      trimmed.startsWith(prefix) &&
      (trimmed.includes('.accessToken=') || trimmed.includes('.idToken='))
    );
  });
};

// Browser-side twin of getServerSideAuth — the same predicate, against
// document.cookie instead of the request header.
//
// Why this exists: the check above is cheap, but living in getServerSideProps
// made EVERY client-side navigation wait on a serverless round-trip
// (`/_next/data/<buildId>/<route>.json`) before Next would even change the URL.
// On a cold Vercel function that is seconds of a button that looks broken —
// nothing moves, nothing spins. Running the identical check in the browser lets
// those pages be statically optimised, so navigation needs no server at all.
//
// The gate is exactly as strong as it was. It was never cryptographic: see the
// note on getServerSideAuth above — a forged cookie always passed it and got
// bounced at the first GraphQL call. Real validation still happens where it
// always did: /api/graphql attaches the JWT and the backend validates the
// signature, and UserInfoContext bounces failures back to /.
export const hasClientSession = () => {
  // On the server there is no cookie jar to read; the browser decides.
  if (typeof document === "undefined") return true;
  if (isDevBypass()) return true;

  const clientId = process.env.USER_POOL_CLIENT_ID;
  if (!clientId) return false;
  const prefix = `CognitoIdentityServiceProvider.${clientId}.`;

  return document.cookie.split(";").some((c) => {
    const trimmed = c.trim();
    return (
      trimmed.startsWith(prefix) &&
      (trimmed.includes(".accessToken=") || trimmed.includes(".idToken="))
    );
  });
};
